import React, { useCallback, useState } from "react";
import {
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  InputGroup,
  ModalFooter,
  Col,
  Alert,
} from "reactstrap";
import { Formik, Form, Field } from "formik";
import { ShareIcon } from "@heroicons/react/20/solid";
import {
  RenderButton,
  RenderTextFieldWithoutAutoComplete,
  SubmitButton,
} from "components";
import { addSharedUser, removeSharedUser, updateCreatorListName } from "../actions";
import { connect } from "react-redux";
import "../index.scss";
import ClipboardWriteButton from "components/ClipboardWriteButton";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

const ShareListModal = ({
  loginUser,
  addSharedUserAction,
  removeSharedUserAction,
  updateCreatorListName,
  creatorList,
  isOpen,
  toggleModal,
  ...props
}) => {
  const [emailAddress, setEmailAddress] = useState("");
  const [allowShareEditing, setAllowShareEditing] = useState(creatorList?.canSharedUsersEdit || false);

  const addSharedUserHandler = () => {
    addSharedUserAction({
      _id: creatorList._id,
      name: creatorList.name,
      shareWithEmail: emailAddress,
    });
    setEmailAddress("");
  };

  const removeSharedUserHandler = (user_id) => {
    removeSharedUserAction({
      _id: creatorList._id,
      shareWithUser_id: user_id,
    });
  };

  const handleUpdateSharingSettings = useCallback((input) => {
    setAllowShareEditing(input.target.checked);
    updateCreatorListName({
      _id: creatorList._id,
      canSharedUsersEdit: input.target.checked,
    });
  }, [setAllowShareEditing, updateCreatorListName, creatorList._id]);

  const sharedUserCol = (user, isOwner) => (
    <Col className="col-12 mt-2 user">
      <h2>
      {user.name}
      {user._id === loginUser?.id && " (You)"}
      </h2>
      <p className="user-email">{user.email}</p>
      {isOwner ? (
        <span className="user-owner">Owner</span>
      ) : (
        <button
          type="button"
          className="close remove-button"
          aria-label="Remove user"
          disabled={creatorList?.listOwner?._id && creatorList?.listOwner?._id !== loginUser?.id}
          onClick={() => removeSharedUserHandler(user._id)}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      )}
    </Col>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggleModal}
      wrapClassName="share-list-modal"
      contentClassName="share-list-content"
      centered
    >
      <ModalHeader>
        <Row className="d-flex align-items-center">
          <ClipboardWriteButton classNames="mr-3" textToCopy={creatorList._id}>
            <span className="export-icon">
              <ShareIcon width={25} height={25} strokeWidth={5} />
            </span>
          </ClipboardWriteButton>
          Share "{creatorList.name}"
        </Row>
      </ModalHeader>
      <ModalBody className="share-modal mb-3">
        {
          creatorList.listOwner?._id && creatorList.listOwner._id !== loginUser?.id && (
            <Row className="g-0 mt-3">
              <Col className="p-0">
                <Alert color="warning" className="d-flex align-items-end"><ExclamationCircleIcon width={25} height={25} strokeWidth={2} className="mr-2" />Only the list owner can change sharing settings</Alert>
              </Col>
            </Row>
          )
        }
        <Row>
          <Formik onSubmit={addSharedUserHandler}>
            {() => (
              <Form className="share-list-form mt-2 mb-4">
                <InputGroup className="d-flex align-items-center">
                  <Field
                    autoFocus
                    id="share-list-field"
                    name="shareEmail"
                    className="col-12"
                    placeholder="Enter email address..."
                    customvalue={emailAddress}
                    disabled={creatorList.listOwner?._id && creatorList.listOwner._id !== loginUser?.id}
                    onChange={(e) => {
                      setEmailAddress(e.target.value);
                    }}
                    component={RenderTextFieldWithoutAutoComplete}
                  />

                  <SubmitButton
                    color="primary"
                    disabled={!emailAddress || (creatorList.listOwner?._id && creatorList.listOwner?._id !== loginUser?.id)}
                    className="ml-3 share-button"
                  >
                    Share
                  </SubmitButton>
                </InputGroup>
              </Form>
            )}
          </Formik>
          <div className="shared-users">
            <h1>People with access</h1>
            <Row className="mx-0">
              {sharedUserCol(creatorList.listOwner, true)}
              {creatorList.sharedWithUser_ids?.map((user) =>
                sharedUserCol(user, false)
              )}
            </Row>
          </div>
        </Row>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between mb-3">
        <div className="form-check pr-5">
          <label
            className="form-check-label"
            htmlFor="allowShareEditing"
          >
            <input
              name="allowShareEditing"
              id="allowShareEditing"
              checked={allowShareEditing}
              onChange={handleUpdateSharingSettings}
              disabled={creatorList.listOwner?._id && creatorList.listOwner?._id !== loginUser?.id}
              className="form-check-input"
              type="checkbox"
            />
            Allow shared users to edit
          </label>
        </div>
        <RenderButton className="btn btn-primary" onClick={toggleModal}>
          Close
        </RenderButton>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    loginUser: state.login.loginUser,
  };
};

export default connect(mapStateToProps, {
  addSharedUserAction: addSharedUser,
  removeSharedUserAction: removeSharedUser,
  updateCreatorListName,
})(ShareListModal);
